import { getRuntimeConfig } from "@/app/config/runtime-config";
import type {
  AdminActingSession,
  AuthenticatedSession,
  MemberStanding,
  RuntimeConfig,
  UserProfile,
} from "@/types/clm-web";
import { MEMBER_STANDINGS } from "@/types/clm-web";
import { resolveActiveRealmContext, resolveRealmAuthBaseUrl, type RealmContext } from "./realm";
import { normalizeRoleClaims } from "./role-access";
import type { AccessTokenResponse } from "./token-manager";

export interface AuthServiceUserResponse {
  sub?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  imis_id?: string;
  member_type?: string;
  person_id?: number;
  display_name?: string;
  roles?: string[];
  /** `AuthUser.auth_realm` — `user` or `admin`. */
  auth_realm?: string;
}

export interface AuthMeResponse {
  authenticated: boolean;
  /** `CurrentUserResponse.auth_realm` — `user` (learner pool), `admin`, or `admin-acting-as`. */
  auth_realm?: string;
  user?: AuthServiceUserResponse;
  roles?: string[];
  /** `CurrentUserResponse.standing` — standard-session UI hint only, not benefit authority. */
  standing?: string;
  acting_as?: {
    active: boolean;
    actual_user: AuthServiceUserResponse;
    effective_user: AuthServiceUserResponse;
    actual_user_roles?: string[];
    effective_user_roles?: string[];
    started_at: string;
    started_from_route?: string;
    return_route: string;
    audit_correlation_id: string;
    acting_session_id?: string;
    status?: AdminActingSession["status"];
  };
}

export interface RefreshSessionResponse {
  success: boolean;
}

export interface LogoutResponse {
  logoutUrl?: string;
}

interface AuthServiceLogoutResponse extends LogoutResponse {
  logout_url?: string;
}

export interface StartActingAsRequest {
  targetPersonId: number;
  startedFromRoute: string;
  returnRoute?: string;
}

export interface AuthClient {
  /** `returnTo` is a same-origin path already normalized by `normalizeReturnTo`. */
  getLoginUrl(returnTo?: string): string;
  getCurrentSession(): Promise<AuthenticatedSession>;
  getAccessToken(): Promise<AccessTokenResponse>;
  refreshSession(): Promise<AuthenticatedSession>;
  logout(): Promise<LogoutResponse>;
  startActingAs(request: StartActingAsRequest): Promise<AuthenticatedSession>;
  stopActingAs(): Promise<AuthenticatedSession>;
}

/**
 * `DeniedPersonRefusalReason` of the auth service OpenAPI (`/auth/refresh` and
 * `/auth/access-token` 401 `code`).
 * Typed here because the generated admin API types do not carry the refusal schema yet.
 */
export const DENIED_PERSON_REFUSAL_REASONS = ["legacy_deleted", "membership_expired"] as const;
export type DeniedPersonRefusalReason = (typeof DENIED_PERSON_REFUSAL_REASONS)[number];

export function isDeniedPersonRefusalReason(value: unknown): value is DeniedPersonRefusalReason {
  return (DENIED_PERSON_REFUSAL_REASONS as readonly unknown[]).includes(value);
}

/** A refresh or token request the auth service refused because the Person is denied (FR-019). */
export class DeniedPersonRefusalError extends Error {
  constructor(readonly code: DeniedPersonRefusalReason) {
    super(`Auth service refused the session: ${code}.`);
    this.name = "DeniedPersonRefusalError";
  }
}

export class AuthClientError extends Error {
  constructor(
    message: string,
    readonly response: Response,
  ) {
    super(message);
    this.name = "AuthClientError";
  }
}

export class HttpAuthClient implements AuthClient {
  private readonly authBaseUrl: string;
  private readonly fetcher: typeof fetch;

  constructor(
    runtimeConfig: RuntimeConfig = getRuntimeConfig(),
    fetcher: typeof fetch = globalThis.fetch,
    realmContext: RealmContext = resolveActiveRealmContext(runtimeConfig),
  ) {
    // Bind so `this.fetcher(...)` is not invoked with the client instance as
    // `this` — browsers throw "TypeError: Illegal invocation" when native
    // fetch runs with a receiver other than window/globalThis, which made
    // every session bootstrap silently resolve to unauthenticated.
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
    this.authBaseUrl = resolveRealmAuthBaseUrl(realmContext);
  }

  getLoginUrl(returnTo?: string): string {
    const url = this.authUrl("/auth/login");
    if (returnTo) {
      url.searchParams.set("returnTo", returnTo);
    }
    return url.toString();
  }

  async getCurrentSession(): Promise<AuthenticatedSession> {
    const response = await this.request<AuthMeResponse>("/auth/me", {
      method: "GET",
    });
    return normalizeAuthMeResponse(response);
  }

  async getAccessToken(): Promise<AccessTokenResponse> {
    return this.request<AccessTokenResponse>(
      "/auth/access-token",
      { method: "GET" },
      throwIfDeniedPersonRefusal,
    );
  }

  async refreshSession(): Promise<AuthenticatedSession> {
    const response = await this.request<AuthMeResponse | RefreshSessionResponse>(
      "/auth/refresh",
      { method: "POST" },
      throwIfDeniedPersonRefusal,
    );

    if (isRefreshSessionResponse(response)) {
      return response.success
        ? this.getCurrentSession()
        : normalizeAuthMeResponse({ authenticated: false });
    }

    return normalizeAuthMeResponse(response);
  }

  async logout(): Promise<LogoutResponse> {
    const response = await this.request<AuthServiceLogoutResponse>("/auth/logout", {
      method: "POST",
      // The auth service records this in the auth_logout audit event; without it
      // the event logs correlation_id=unknown.
      headers: { "x-correlation-id": crypto.randomUUID() },
    });

    return normalizeLogoutResponse(response);
  }

  async startActingAs(request: StartActingAsRequest): Promise<AuthenticatedSession> {
    const response = await this.request<AuthMeResponse>("/auth/admin/impersonation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        target_person_id: request.targetPersonId,
        started_from_route: request.startedFromRoute,
        return_route: request.returnRoute ?? "/admin/people",
      }),
    });

    return normalizeAuthMeResponse(response);
  }

  async stopActingAs(): Promise<AuthenticatedSession> {
    const response = await this.request<AuthMeResponse>("/auth/admin/impersonation", {
      method: "DELETE",
    });
    return normalizeAuthMeResponse(response);
  }

  private async request<T>(
    path: `/${string}`,
    init: RequestInit,
    onUnauthorized?: (response: Response) => Promise<void>,
  ): Promise<T> {
    const response = await this.fetcher(this.authUrl(path), {
      credentials: "include",
      ...init,
    });

    if (response.status === 401) {
      await onUnauthorized?.(response);
      return { authenticated: false } as T;
    }

    if (!response.ok) {
      throw new AuthClientError(
        `Auth service request failed with status ${response.status}.`,
        response,
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    const body = await response.text();
    if (body.trim().length === 0) {
      return {} as T;
    }

    return JSON.parse(body) as T;
  }

  private authUrl(path: `/${string}`): URL {
    return new URL(path, this.authBaseUrl);
  }
}

// Boundary parser for the `/auth/refresh` and `/auth/access-token` 401 problem body: only an own
// `code` that is one of the two refusal reasons is a refusal; anything else is an ordinary 401.
async function throwIfDeniedPersonRefusal(response: Response): Promise<void> {
  let body: unknown;
  try {
    body = JSON.parse(await response.text());
  } catch {
    return;
  }

  if (typeof body !== "object" || body === null || !Object.hasOwn(body, "code")) {
    return;
  }

  const { code } = body as { code: unknown };
  if (isDeniedPersonRefusalReason(code)) {
    throw new DeniedPersonRefusalError(code);
  }
}

export function normalizeAuthMeResponse(response: AuthMeResponse): AuthenticatedSession {
  if (!response.authenticated) {
    return {
      authenticated: false,
      roleClaims: [],
      sessionCookieManagedByAuthService: true,
    };
  }

  const user = response.user ? normalizeUser(response.user) : undefined;
  const roleClaims = normalizeRoleClaims(response.roles ?? response.user?.roles ?? []);

  // 004-6 clarification 2026-10-04 (CLMS-158 T051): a user-realm session is the
  // spec's "signed-in learner" (FR-026). The user realm projects `roles` only from
  // Cognito groups and the sandbox learner pool defines none, so learners arrive
  // with no raw `member` claim; the `includes` guard keeps any claim a pool does
  // produce. The courses API admits the same session by realm; legacy admitted
  // any signed-in role to the member area (DynamicAuthorize deny-list). Standing
  // (`pre_activation`) is projected separately and still narrows the shell.
  if (
    (response.auth_realm ?? response.user?.auth_realm) === "user" &&
    !roleClaims.includes("member")
  ) {
    roleClaims.unshift("member");
  }

  // Per the auth-session runtime contract, an authenticated /auth/me response is the
  // authoritative session signal even when no recognized business roles are present
  // (e.g. admin-realm sessions whose only raw role is a Cognito federation group).
  if (!roleClaims.includes("authenticated")) {
    roleClaims.push("authenticated");
  }

  const standing = normalizeStanding(response.standing);

  return {
    authenticated: true,
    ...(user ? { user } : {}),
    roleClaims,
    sessionCookieManagedByAuthService: true,
    ...(standing ? { standing } : {}),
    ...(response.acting_as?.active
      ? { actingSession: normalizeActingSession(response.acting_as) }
      : {}),
  };
}

function isMemberStanding(value: string): value is MemberStanding {
  return (MEMBER_STANDINGS as readonly string[]).includes(value);
}

/**
 * Absent standing = older auth build → normal shell (the API denies
 * server-side). A value outside the closed enum is not a standing this build
 * understands: it is dropped rather than mapped to `normal`, so the shell
 * falls back to the normal shell only through the same absent-standing path
 * and never fabricates a restriction it cannot name.
 */
function normalizeStanding(value: string | undefined): MemberStanding | undefined {
  return value !== undefined && isMemberStanding(value) ? value : undefined;
}

function isRefreshSessionResponse(
  response: AuthMeResponse | RefreshSessionResponse,
): response is RefreshSessionResponse {
  return "success" in response;
}

function normalizeLogoutResponse(response: AuthServiceLogoutResponse): LogoutResponse {
  return {
    logoutUrl: response.logoutUrl ?? response.logout_url,
  };
}

function normalizeUser(user: AuthServiceUserResponse): UserProfile {
  return {
    sub: user.sub ?? user.email ?? "",
    email: user.email ?? "",
    firstName: user.first_name,
    lastName: user.last_name,
    imisId: user.imis_id,
    memberType: user.member_type,
    personId: user.person_id,
    displayName: user.display_name,
    roles: user.roles ? normalizeRoleClaims(user.roles) : undefined,
  };
}

function normalizeActingSession(
  actingAs: NonNullable<AuthMeResponse["acting_as"]>,
): AdminActingSession {
  return {
    actingSessionId: actingAs.acting_session_id ?? actingAs.audit_correlation_id,
    actualUser: normalizeUser(actingAs.actual_user),
    effectiveUser: normalizeUser(actingAs.effective_user),
    actualUserRoles: normalizeRoleClaims(
      actingAs.actual_user_roles ?? actingAs.actual_user.roles ?? [],
    ),
    effectiveUserRoles: normalizeRoleClaims(
      actingAs.effective_user_roles ?? actingAs.effective_user.roles ?? [],
    ),
    startedAt: actingAs.started_at,
    startedFromRoute: actingAs.started_from_route ?? "/admin/people",
    returnRoute: actingAs.return_route,
    auditCorrelationId: actingAs.audit_correlation_id,
    status: actingAs.status ?? "active",
  };
}
